"use client";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { LoginMethodTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useInitGlobalState } from "@/hooks/initGlobalState";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useLogout } from "@/hooks/useLogout";
import { deleteResetLastViewedCookie } from "@/utils/functions/cookieOperations";
import { useSetAtom } from "jotai";
import { latestViewedProductsAtom } from "@/hooks/productAtoms";
import { RESET } from "jotai/utils";

const LOCAL_CACHE_TIMESTAMP_KEY = "last_global_cache_reset_timestamp";

type Props = {
  serverCacheResetTimestamp?: number;
  /** Currency the server resolved for this visitor and rendered the prices in. */
  serverCurrencyCode?: string;
};

const InitGlobaleStateLeaf = ({
  serverCacheResetTimestamp,
  serverCurrencyCode,
}: Props) => {
  const { data } = useGetCompanyQuery();
  const pathname = usePathname();
  const { logout } = useLogout();
  useInitGlobalState(serverCurrencyCode);

  const setLatestViewed = useSetAtom(latestViewedProductsAtom);

  useEffect(() => {
    if (
      data?.result?.theme?.settings?.customer_login_methods ===
      LoginMethodTypes.none
    ) {
      logout();
    }
  }, [data, logout, pathname]);

  useEffect(() => {
    if (!serverCacheResetTimestamp) return;

    const localTimestamp = localStorage.getItem(LOCAL_CACHE_TIMESTAMP_KEY);
    const lastReset = localTimestamp ? parseInt(localTimestamp, 10) : 0;

    if (serverCacheResetTimestamp > lastReset) {
      // Clear cache actions
      setLatestViewed(RESET);
      deleteResetLastViewedCookie();

      // Update local timestamp
      localStorage.setItem(
        LOCAL_CACHE_TIMESTAMP_KEY,
        serverCacheResetTimestamp.toString(),
      );

      // Confirm reset to server (delete key)
      fetch("/clear-cache/reset-timestamp", {
        method: "POST",
        keepalive: true,
      }).catch((err) => console.error("Failed to confirm cache reset:", err));
    }
  }, [serverCacheResetTimestamp, setLatestViewed]);

  return <></>;
};

export default InitGlobaleStateLeaf;
